import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, BookOpen, Plus, Loader2, ArrowRight, AlertTriangle,
  CalendarClock, MapPin, Inbox, Clock, CheckCircle2, TrendingUp, Zap, FileCode2,
  ShieldAlert, PenLine, RefreshCw, Bot, HeartHandshake,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { PlannerOutcomes } from '../components/brand/PlannerOutcomes';
import { OneClickDischarge } from '../components/brand/OneClickDischarge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Icd10ExtractPanel } from '../components/matching/Icd10ExtractPanel';
import { EhrDropZone } from '../components/matching/EhrDropZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];
const CARE_TYPES = ['Skilled nursing (RN/LPN)', 'Personal care / aide (CNA)', 'Physical / Occupational therapy', 'Hospice & palliative', 'Companion care', 'Not sure — need guidance'];

// Pipeline columns for the planner board. Each column carries a `keys` array
// of every DB status that falls into it, so we render referrals from BOTH the
// legacy state machine (new/contacted/placed) and the new discharge-orchestration
// set (intake/matching/first_visit_committed/active/escalated/cancelled) defined
// in /app/backend/routes/discharge/_helpers.py::REFERRAL_STATUSES. Without this,
// ~28% of production referrals (status='matching', 'first_visit_committed', etc.)
// vanish from the board — QA finding, Iter-113.
const COLUMNS = [
  { key: 'new',       keys: ['new', 'intake'],                             label: 'New',       accent: 'border-t-blue-400' },
  { key: 'matching',  keys: ['contacted', 'matching'],                     label: 'Matching',  accent: 'border-t-amber-400' },
  { key: 'matched',   keys: ['matched'],                                   label: 'Matched',   accent: 'border-t-violet-400' },
  { key: 'placed',    keys: ['placed', 'first_visit_committed', 'active'], label: 'In care',   accent: 'border-t-emerald-500' },
  { key: 'escalated', keys: ['escalated'],                                 label: 'Escalated', accent: 'border-t-rose-500' },
  { key: 'closed',    keys: ['closed', 'cancelled'],                       label: 'Closed',    accent: 'border-t-slate-300' },
];

// Full set of DB statuses the Select must expose so a planner can transition a
// referral to ANY legal state. Sourced from backend REFERRAL_STATUSES + the
// three legacy values still on some historical documents.
const STATUS_OPTIONS = [
  { value: 'new',                    label: 'New' },
  { value: 'intake',                 label: 'Intake' },
  { value: 'contacted',              label: 'Contacted' },
  { value: 'matching',               label: 'Matching' },
  { value: 'matched',                label: 'Matched' },
  { value: 'first_visit_committed',  label: 'First visit committed' },
  { value: 'active',                 label: 'Active (in care)' },
  { value: 'placed',                 label: 'Placed' },
  { value: 'escalated',              label: 'Escalated' },
  { value: 'closed',                 label: 'Closed' },
  { value: 'cancelled',              label: 'Cancelled' },
];

// Which statuses count toward the "In progress" header stat. Any referral
// that's between initial receipt and terminal-close counts here.
const IN_PROGRESS_STATUSES = [
  'contacted', 'matching', 'matched',
  'first_visit_committed', 'active',
];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="cn-card rounded-2xl p-5" data-testid={`planner-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-display font-semibold text-cn-text">{value}</div>
    <div className="text-sm text-cn-text-muted mt-0.5">{label}</div>
  </div>
);

const fmtAge = (days) => days == null ? '' : days === 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;

// ── Phase 7 Prompt 2 — lead with exceptions, not lists ──────────────────
// Top strip: everything currently blocking a discharge, each card with
// the ONE action that unblocks it. Sourced from /planner/action-queue.
const CARD_TONE = {
  coverage_hold:      { icon: ShieldAlert, tone: 'border-red-200 bg-red-50/60',    chip: 'bg-red-100 text-red-800' },
  signature_pending:  { icon: PenLine,     tone: 'border-amber-200 bg-amber-50/60', chip: 'bg-amber-100 text-amber-800' },
  stalled_offer:      { icon: Clock,       tone: 'border-orange-200 bg-orange-50/60', chip: 'bg-orange-100 text-orange-800' },
  signature_declined: { icon: AlertTriangle, tone: 'border-rose-200 bg-rose-50/60', chip: 'bg-rose-100 text-rose-800' },
  escalated:          { icon: AlertTriangle, tone: 'border-violet-200 bg-violet-50/60', chip: 'bg-violet-100 text-violet-800' },
  gate:               { icon: Bot,         tone: 'border-slate-200 bg-slate-50/60', chip: 'bg-slate-100 text-slate-700' },
};

function PlannerActionQueue() {
  const { getAuthHeader } = useAuth();
  const [queue, setQueue] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planner/action-queue`, getAuthHeader());
      setQueue(res.data);
    } catch (e) { setQueue({ count: 0, cards: [] }); }
  }, [getAuthHeader]);
  useEffect(() => { load(); }, [load]);

  const resolveCoverage = async (card, decision) => {
    setBusy(card.gate_id);
    try {
      await axios.post(`${API}/orchestrator/gates/${card.gate_id}/approve`,
        { trace_id: card.trace_id, decision, note: `coverage ${decision} from planner action queue` },
        getAuthHeader());
      toast.success(decision === 'approved' ? 'Coverage cleared — loop resumed' : 'Coverage rejected — referral escalated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resolve gate');
    } finally { setBusy(null); }
  };

  const advanceWave = async (card) => {
    setBusy(card.trace_id);
    try {
      await axios.post(`${API}/orchestrator/traces/${card.trace_id}/advance-wave`, {}, getAuthHeader());
      toast.success('Next offer wave sent');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not advance wave');
    } finally { setBusy(null); }
  };

  if (!queue) return null;
  if (queue.count === 0) {
    return (
      <div className="cn-card rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="planner-action-queue-empty">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <span className="text-sm text-cn-text-muted">No exceptions — your referrals are moving. Coverage, signatures and offers are all clear.</span>
      </div>
    );
  }
  return (
    <div className="mb-6" data-testid="planner-action-queue">
      <div className="flex items-center justify-between mb-3">
        <h2 className="cn-display text-lg text-cn-text inline-flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-600" /> Action queue ({queue.count})
        </h2>
        <button onClick={load} className="text-xs text-cn-text-muted hover:text-cn-primary inline-flex items-center gap-1" data-testid="action-queue-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {queue.cards.slice(0, 9).map((c, i) => {
          const t = CARD_TONE[c.type] || CARD_TONE.gate;
          const Icon = t.icon;
          const key = c.gate_id || c.doc_id || `${c.type}-${c.referral_id || i}`;
          return (
            <div key={key} className={`rounded-2xl border p-4 ${t.tone}`} data-testid={`action-card-${c.type}-${i}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className="w-4 h-4" />
                <span className="font-semibold text-sm">{c.title}</span>
                {c.simulated && (
                  <span className="text-[0.6rem] px-1.5 py-0.5 rounded bg-white/70 border border-slate-200 text-slate-600" data-testid={`simulated-badge-${i}`}>
                    verification: simulated (pilot)
                  </span>
                )}
              </div>
              <p className="text-xs text-cn-text-muted mb-3 line-clamp-2">{c.detail}</p>
              <div className="flex gap-2 flex-wrap">
                {(c.actions || []).includes('approve_coverage') && (
                  <>
                    <button onClick={() => resolveCoverage(c, 'approved')} disabled={busy === c.gate_id}
                      className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 font-medium"
                      data-testid={`approve-coverage-${i}`}>
                      {busy === c.gate_id ? '…' : 'Override & resume'}
                    </button>
                    <button onClick={() => resolveCoverage(c, 'rejected')} disabled={busy === c.gate_id}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-red-300 text-red-700 hover:bg-red-50 font-medium"
                      data-testid={`reject-coverage-${i}`}>
                      Reject
                    </button>
                  </>
                )}
                {(c.actions || []).includes('advance_wave') && (
                  <button onClick={() => advanceWave(c)} disabled={busy === c.trace_id}
                    className="text-xs px-3 py-1.5 rounded-full bg-cn-primary text-white font-medium"
                    data-testid={`advance-wave-${i}`}>
                    {busy === c.trace_id ? '…' : 'Send next offer'}
                  </button>
                )}
                {(c.actions || []).includes('open_orchestrator') && (
                  <a href="/planner/orchestrator" className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
                    data-testid={`open-orchestrator-${i}`}>
                    Review & sign off
                  </a>
                )}
                {(c.actions || []).includes('review_documents') && (
                  <a href="/provider-os/documents" className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
                    data-testid={`review-docs-${i}`}>
                    Review document
                  </a>
                )}
                {(c.actions || []).includes('open_referrals') && (
                  <a href="/spine/discharge-planner" className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
                    data-testid={`open-referrals-${i}`}>
                    Open referral
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Loop-stage pipeline: intake → matching → accepted → coverage cleared →
// docs signed → first-visit committed → watching. Counts click through.
function LoopStagesRow() {
  const { getAuthHeader } = useAuth();
  const [pipe, setPipe] = useState(null);
  const [openStage, setOpenStage] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/planner/loop-pipeline`, getAuthHeader())
      .then((res) => { if (live) setPipe(res.data); })
      .catch(() => { if (live) setPipe(null); });
    return () => { live = false; };
  }, [getAuthHeader]);

  if (!pipe) return null;
  return (
    <div className="cn-card rounded-2xl p-4 mb-6" data-testid="planner-loop-stages">
      <div className="text-xs font-semibold uppercase tracking-wide text-cn-text-muted mb-3">
        Discharge loop — {pipe.total} in flight
      </div>
      <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
        {pipe.stages.map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <div className="self-center text-cn-text-muted/40 px-0.5">→</div>}
            <button
              onClick={() => setOpenStage(openStage === s.key ? null : s.key)}
              className={`flex-1 min-w-[92px] rounded-xl border px-2 py-2 text-center transition-colors ${
                openStage === s.key ? 'border-cn-primary bg-cn-soft/60' :
                s.count > 0 ? 'border-cn-border bg-white dark:bg-cn-ink-800 hover:border-cn-primary/40' : 'border-cn-border/50 bg-cn-soft/20 opacity-60'
              }`}
              data-testid={`loop-stage-${s.key}`}
            >
              <div className="text-xl font-display font-semibold text-cn-text">{s.count}</div>
              <div className="text-[0.65rem] text-cn-text-muted leading-tight">{s.label}</div>
            </button>
          </React.Fragment>
        ))}
      </div>
      {openStage && (
        <div className="mt-3 pt-3 border-t border-cn-border/50 text-xs text-cn-text-muted" data-testid={`loop-stage-detail-${openStage}`}>
          {(() => {
            const s = pipe.stages.find((x) => x.key === openStage);
            if (!s || s.count === 0) return <span>No referrals at this stage.</span>;
            return (
              <span>
                {s.count} referral{s.count === 1 ? '' : 's'} — sample:{' '}
                {s.referral_ids.map((id) => (
                  <a key={id} href="/spine/discharge-planner" className="font-mono text-cn-primary hover:underline mr-2">
                    {id.slice(0, 8)}…
                  </a>
                ))}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
}


const ReferralCard = ({ r, onStatusChange, busy }) => {
  const [status, setStatus] = useState(r.status || 'new');
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const dirty = status !== (r.status || 'new') || note.trim().length > 0;
  const history = r.status_history || [];

  return (
    <div className="cn-card rounded-2xl p-4 bg-white dark:bg-cn-ink-800" data-testid={`planner-referral-${r.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-cn-text">{r.patient_initials || 'Patient'}</span>
        {r.urgency === 'urgent' && (
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500 text-white inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Urgent
          </span>
        )}
      </div>
      {r.needs_attention && (
        <div className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 rounded-lg px-2 py-1 inline-flex items-center gap-1" data-testid={`referral-attention-${r.id}`}>
          <AlertTriangle className="w-3 h-3" /> {r.attention_reason}
        </div>
      )}
      {r.primary_diagnosis && <p className="text-sm text-cn-text mt-1">{r.primary_diagnosis}</p>}
      {r.care_type && <p className="text-xs text-cn-text-muted mt-0.5">{r.care_type}</p>}
      <div className="mt-3 space-y-1 text-xs text-cn-text-muted">
        {(r.city || r.state) && <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[r.city, r.state].filter(Boolean).join(', ')}</div>}
        {r.discharge_date && <div className="inline-flex items-center gap-1 ml-2"><CalendarClock className="w-3 h-3" /> {r.discharge_date}</div>}
      </div>
      <div className="text-[11px] text-cn-text-muted/70 mt-2">Submitted {fmtAge(r.age_days)}</div>

      {/* Status management */}
      <div className="mt-3 pt-3 border-t border-cn-border/50 space-y-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 rounded-xl text-sm" data-testid={`referral-status-select-${r.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {showNote ? (
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note (optional)…" className="text-sm rounded-xl" data-testid={`referral-note-${r.id}`} />
        ) : (
          <button onClick={() => setShowNote(true)} className="text-[11px] font-semibold text-cn-primary hover:underline" data-testid={`referral-add-note-${r.id}`}>+ Add a note</button>
        )}
        {dirty && (
          <button
            onClick={() => { onStatusChange(r.id, status, note); setNote(''); setShowNote(false); }}
            disabled={busy === r.id}
            className="cn-btn cn-btn-primary w-full !py-2 text-sm"
            data-testid={`referral-update-${r.id}`}
          >
            {busy === r.id ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <>Update</>}
          </button>
        )}
        {history.length > 0 && (
          <details className="text-[11px] text-cn-text-muted" data-testid={`referral-history-${r.id}`}>
            <summary className="cursor-pointer font-semibold hover:text-cn-primary">History ({history.length})</summary>
            <ul className="mt-1.5 space-y-1">
              {history.slice(-4).reverse().map((h, i) => (
                <li key={i} className="flex flex-col">
                  <span><span className="font-semibold capitalize">{h.status}</span> · {new Date(h.at).toLocaleDateString()} ({h.by_role})</span>
                  {h.note && <span className="italic text-cn-text-muted/80">“{h.note}”</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
};

/**
 * ReferralModal — planner referral intake dialog.
 *
 * ⚠️ Iter 158.2 architectural note: This component MUST keep using
 * shadcn/ui <Dialog> (which renders via a Radix portal to document.body)
 * so it overlays correctly even when the wrapping <PlannerDashboard/>
 * subtree is display:none in DashboardFocusShell focus mode. If this
 * modal is ever refactored to inline (non-portal) rendering, the
 * ?refer=1 deep-link CTA from the focus panel will silently break —
 * the modal would be display:none-hidden along with its parent.
 */
const ReferralModal = ({ open, onClose, onCreated, user }) => {
  const { getAuthHeader } = useAuth();
  const empty = {
    patient_initials: '', primary_diagnosis: '', care_type: '', discharge_date: '',
    city: '', state: '', insurance: '', hours_needed: '', urgency: 'standard', notes: '',
    icd10_codes: [],
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const applyIcd10 = (codes, primaryDescription) => {
    setForm((f) => ({
      ...f,
      icd10_codes: codes,
      // Only overwrite the primary diagnosis text if the planner
      // hasn't typed something themselves.
      primary_diagnosis: f.primary_diagnosis.trim().length > 0
        ? f.primary_diagnosis
        : primaryDescription,
    }));
  };

  const applyEhr = (extracted) => {
    // Iter 149 — hydrate every empty form field from the EHR file.
    // Never clobber planner-typed values.
    if (!extracted || typeof extracted !== 'object') return;
    setForm((f) => {
      const next = { ...f };
      const fillString = (k) => {
        if (extracted[k] && !next[k]?.toString?.().trim()) {
          next[k] = extracted[k];
        }
      };
      fillString('patient_initials');
      fillString('primary_diagnosis');
      fillString('city');
      fillString('state');
      fillString('insurance');
      fillString('discharge_date');
      fillString('care_type');
      fillString('urgency');
      fillString('notes');
      // ICD-10 codes: only when the form currently has none.
      if (Array.isArray(extracted.icd10_codes)
          && extracted.icd10_codes.length
          && (!next.icd10_codes || next.icd10_codes.length === 0)) {
        next.icd10_codes = extracted.icd10_codes;
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/referrals`, {
        ...form,
        planner_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Discharge planner',
        email: user?.email,
      }, getAuthHeader());
      toast.success('Referral submitted — our concierge team is on it.');
      setForm(empty);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit referral.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="planner-referral-modal">
        <DialogHeader>
          <DialogTitle className="cn-display text-2xl">Refer a patient</DialogTitle>
          <DialogDescription>
            This will be linked to your account so you can track placement on your pipeline. Please use patient
            <span className="font-medium"> initials only</span>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="planner-referral-form">
          <EhrDropZone onExtracted={applyEhr} disabled={submitting} />
          <Icd10ExtractPanel onApply={applyIcd10} disabled={submitting} />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p_initials">Patient initials</Label>
              <Input id="p_initials" value={form.patient_initials} onChange={set('patient_initials')} placeholder="R.S." className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-initials" />
            </div>
            <div>
              <Label htmlFor="p_discharge" className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Discharge date</Label>
              <Input id="p_discharge" type="date" value={form.discharge_date} onChange={set('discharge_date')} className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-discharge" />
            </div>
            <div>
              <Label>Care type</Label>
              <Select value={form.care_type} onValueChange={set('care_type')}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-care-type"><SelectValue placeholder="Select care type" /></SelectTrigger>
                <SelectContent>{CARE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="p_dx">Primary diagnosis</Label>
              <Input id="p_dx" value={form.primary_diagnosis} onChange={set('primary_diagnosis')} placeholder="CHF, post-op hip…" className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-diagnosis" />
              {form.icd10_codes?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="planner-icd10-chips">
                  {form.icd10_codes.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 text-xs px-2.5 py-1 font-mono border border-indigo-200"
                    >
                      {c}
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, icd10_codes: f.icd10_codes.filter(x => x !== c) }))}
                        className="text-indigo-500 hover:text-indigo-800"
                        aria-label={`Remove ${c}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="p_city">City</Label>
              <Input id="p_city" value={form.city} onChange={set('city')} placeholder="Austin" className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-city" />
            </div>
            <div>
              <Label>State</Label>
              <Select value={form.state} onValueChange={set('state')}>
                <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-state"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="p_hours">Hours needed</Label>
              <Input id="p_hours" value={form.hours_needed} onChange={set('hours_needed')} placeholder="20/week, overnight…" className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-hours" />
            </div>
            <div>
              <Label htmlFor="p_ins">Insurance / payer</Label>
              <Input id="p_ins" value={form.insurance} onChange={set('insurance')} placeholder="Medicare, Aetna…" className="mt-1.5 h-11 rounded-xl" data-testid="planner-ref-insurance" />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Urgency</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { v: 'standard', l: 'Standard' },
                { v: 'priority', l: 'Priority' },
                { v: 'urgent',   l: 'Urgent (discharging soon)' },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('urgency')(o.v)}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors ${form.urgency === o.v ? 'bg-cn-primary text-white border-cn-primary' : 'bg-cn-bg text-cn-text border-cn-border hover:border-cn-primary/40'}`}
                  data-testid={`planner-ref-urgency-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="p_notes">Notes</Label>
            <Textarea id="p_notes" value={form.notes} onChange={set('notes')} placeholder="Equipment, mobility, languages, family contacts…" className="mt-1.5 rounded-xl min-h-[80px]" data-testid="planner-ref-notes" />
          </div>
          <button type="submit" disabled={submitting} className="cn-btn cn-btn-primary w-full justify-center !py-3" data-testid="planner-ref-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit referral <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function PlannerDashboard() {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState({ referrals: [], summary: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('pipeline'); // 'pipeline' | 'outcomes'

  // Deep-link: /planner/dashboard?refer=1 opens the "Refer a patient" modal directly
  // (used by Cara's role-aware quick action). Strip the param once consumed.
  useEffect(() => {
    if (searchParams.get('refer') === '1') {
      setModalOpen(true);
      searchParams.delete('refer');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planner/referrals`, getAuthHeader());
      setData(res.data || { referrals: [], summary: {}, total: 0 });
    } catch (e) {
      toast.error('Failed to load your referrals');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  const [savingId, setSavingId] = useState(null);
  const updateStatus = async (id, status, note) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/planner/referrals/${id}/status`, { status, note: note || null }, getAuthHeader());
      toast.success('Referral updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update referral');
    } finally { setSavingId(null); }
  };

  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

  const s = data.summary || {};
  const inProgress = IN_PROGRESS_STATUSES.reduce((n, k) => n + (s[k] || 0), 0);
  const placedCount = (s.placed || 0) + (s.first_visit_committed || 0) + (s.active || 0);
  const newCount = (s.new || 0) + (s.intake || 0);

  const navItems = [
    { label: 'One-Click Discharge', icon: Zap, onClick: () => setView('discharge'), active: view === 'discharge' },
    { label: 'FHIR Encounter Ingest', icon: FileCode2, href: '/referrals/ingest-fhir' },
    { label: 'Pipeline', icon: LayoutDashboard, onClick: () => setView('pipeline'), active: view === 'pipeline' },
    { label: 'Orchestrator', icon: Bot, href: '/planner/orchestrator' },
    { label: 'Advance Care Planning', icon: HeartHandshake, href: '/planner/acp' },
    { label: 'Outcomes', icon: TrendingUp, onClick: () => setView('outcomes'), active: view === 'outcomes' },
    { label: 'Find clinicians', icon: Users, href: '/spine/scoreboard' },
    { label: 'Team', icon: Users, href: '/org/team' },
    { label: 'Resources', icon: BookOpen, href: '/help' },
  ];

  return (
    <DashboardShell
      title={`Welcome, ${user?.first_name || 'there'}`}
      subtitle="Send referrals via FHIR or one-click, route patients in-network, and track placement from discharge to care."
      navItems={navItems}
      testId="planner-dashboard"
      rightSlot={
        <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="planner-refer-btn">
          <Plus className="w-4 h-4" /> Refer a patient
        </button>
      }
    >
      {/* View toggle */}
      <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-cn-soft/50 border border-cn-border mb-6" data-testid="planner-view-toggle">
        {[{ k: 'discharge', label: 'One-Click Discharge' }, { k: 'pipeline', label: 'Pipeline' }, { k: 'outcomes', label: 'Outcomes' }].map((t) => (
          <button
            key={t.k}
            onClick={() => setView(t.k)}
            data-testid={`view-${t.k}`}
            className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${view === t.k ? 'bg-white dark:bg-cn-ink-800 shadow-sm text-cn-primary' : 'text-cn-text-muted hover:text-cn-text'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {view === 'discharge' ? (
        <OneClickDischarge onReferred={load} />
      ) : view === 'outcomes' ? (
        <PlannerOutcomes onRecorded={load} />
      ) : (
      <>
      {/* Phase 7 — exceptions first, then the loop, then the board */}
      <PlannerActionQueue />
      <LoopStagesRow />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Inbox} label="Total referrals" value={data.total} tone="bg-cn-soft text-cn-primary" />
        <StatCard icon={Clock} label="Awaiting review" value={newCount} tone="bg-blue-100 text-blue-600" />
        <StatCard icon={ArrowRight} label="In progress" value={inProgress} tone="bg-amber-100 text-amber-600" />
        <StatCard icon={CheckCircle2} label="Placed" value={placedCount} tone="bg-emerald-100 text-emerald-600" />
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>
      ) : data.total === 0 ? (
        <div className="cn-card rounded-3xl p-10 text-center" data-testid="planner-empty">
          <div className="w-14 h-14 rounded-2xl bg-cn-soft flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-cn-primary" />
          </div>
          <h3 className="cn-display text-2xl text-cn-text mb-2">No referrals yet</h3>
          <p className="text-cn-text-muted max-w-md mx-auto mb-6">
            Refer your first patient and our concierge team will match them with verified clinicians — usually within 24–48 hours.
          </p>
          <button onClick={() => setModalOpen(true)} className="cn-btn cn-btn-primary" data-testid="planner-empty-refer-btn">
            <Plus className="w-4 h-4" /> Refer a patient
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="cn-display text-xl text-cn-text">Your referral pipeline</h2>
            {data.needs_attention > 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="planner-needs-attention">
                <AlertTriangle className="w-3.5 h-3.5" /> {data.needs_attention} need{data.needs_attention === 1 ? 's' : ''} attention
              </span>
            )}
          </div>
          <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="planner-pipeline">
            {COLUMNS.map((col) => {
              const items = (data.referrals || []).filter((r) => col.keys.includes(r.status || 'new'));
              return (
                <div key={col.key} className={`rounded-2xl bg-cn-soft/30 dark:bg-white/5 p-3 border-t-4 ${col.accent}`} data-testid={`planner-col-${col.key}`}>
                  <div className="flex items-center justify-between px-1 mb-3">
                    <span className="text-sm font-semibold text-cn-text">{col.label}</span>
                    <span className="text-xs font-bold text-cn-text-muted bg-white dark:bg-cn-ink-800 rounded-full px-2 py-0.5">{items.length}</span>
                  </div>
                  <div className="space-y-3 min-h-[40px]">
                    {items.map((r) => <ReferralCard key={r.id} r={r} onStatusChange={updateStatus} busy={savingId} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      )}

      <ReferralModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={load} user={user} />
    </DashboardShell>
  );
}
